/**
 * Barcode Types
 * Centralized enum and interfaces for all barcode-related functionality
 *
 * DRY Compliance: Single source of truth for barcode types across the platform
 * Rule 15 Compliance: Proper type definitions with validation
 *
 * Used by:
 * - Vendor-specific barcode configurations
 * - Product barcode management
 * - Label generation
 * - Receiving/putaway scanning
 * - Inventory tracking
 */

// ============================================================================
// BARCODE TYPE ENUMS
// ============================================================================

/**
 * Supported barcode types
 * Includes linear, 2D, and GS1 standard barcodes
 */
export enum BarcodeType {
  // Linear Barcodes
  CODE128 = 'CODE128',
  CODE39 = 'CODE39',
  CODE93 = 'CODE93',
  EAN_13 = 'EAN_13',
  EAN_8 = 'EAN_8',
  UPC_A = 'UPC_A',
  UPC_E = 'UPC_E',
  ITF_14 = 'ITF_14',

  // GS1 Standards
  GS1_128 = 'GS1_128', // Formerly UCC/EAN-128
  GS1_DATAMATRIX = 'GS1_DATAMATRIX',
  GS1_QR = 'GS1_QR',

  // 2D Barcodes
  QR_CODE = 'QR_CODE',
  DATAMATRIX = 'DATAMATRIX',
  PDF417 = 'PDF417',

  // Specialized
  PHARMACODE = 'PHARMACODE',
  AZTEC = 'AZTEC',
  CODABAR = 'CODABAR',
  CODE11 = 'CODE11',
}

/**
 * Barcode usage context
 * Determines where and how the barcode is used
 */
export enum BarcodeUsage {
  PRODUCT_IDENTIFICATION = 'PRODUCT_IDENTIFICATION',
  SHIPPING_CONTAINER = 'SHIPPING_CONTAINER',
  LOGISTICS_UNIT = 'LOGISTICS_UNIT',
  SERIAL_NUMBER = 'SERIAL_NUMBER',
  LOT_TRACKING = 'LOT_TRACKING',
  LOCATION = 'LOCATION',
  ASSET = 'ASSET',
  PALLET = 'PALLET',
  CARTON = 'CARTON',
}

/**
 * Vendor-specific barcode usage context
 * Determines where and how vendor barcode configurations are applied
 */
export enum VendorBarcodeUsage {
  PRODUCT_IDENTIFICATION = 'PRODUCT_IDENTIFICATION',
  SHIPPING_CONTAINER = 'SHIPPING_CONTAINER',
  LOGISTICS_UNIT = 'LOGISTICS_UNIT',
  SERIAL_NUMBER = 'SERIAL_NUMBER',
  LOT_TRACKING = 'LOT_TRACKING',
  PALLET = 'PALLET',
  CARTON = 'CARTON',
}

/**
 * Vendor barcode usage labels for UI
 */
export const VendorBarcodeUsageLabels: Record<VendorBarcodeUsage, string> = {
  [VendorBarcodeUsage.PRODUCT_IDENTIFICATION]: 'Product Identification',
  [VendorBarcodeUsage.SHIPPING_CONTAINER]: 'Shipping Container',
  [VendorBarcodeUsage.LOGISTICS_UNIT]: 'Logistics Unit',
  [VendorBarcodeUsage.SERIAL_NUMBER]: 'Serial Number',
  [VendorBarcodeUsage.LOT_TRACKING]: 'Lot/Batch Tracking',
  [VendorBarcodeUsage.PALLET]: 'Pallet',
  [VendorBarcodeUsage.CARTON]: 'Carton',
};

// ============================================================================
// BARCODE INTERFACES
// ============================================================================

/**
 * Barcode format configuration
 * Used to define validation rules and format specifications
 */
export interface BarcodeFormatConfig {
  /** Barcode type */
  type: BarcodeType;
  /** Prefix to prepend to barcode value */
  prefix?: string;
  /** Suffix to append to barcode value */
  suffix?: string;
  /** Fixed length for barcode (null if variable) */
  length?: number;
  /** Whether to validate checksum */
  validateChecksum?: boolean;
  /** Regex pattern for validation */
  pattern?: string;
  /** Character set allowed */
  characterSet?: 'NUMERIC' | 'ALPHANUMERIC' | 'FULL_ASCII';
  /** Whether this is a GS1 barcode with AIs */
  isGS1?: boolean;
}

/**
 * Barcode validation result
 */
export interface BarcodeValidationResult {
  /** Whether the barcode is valid */
  valid: boolean;
  /** Detected barcode type (if auto-detected) */
  type?: BarcodeType;
  /** Error message if invalid */
  error?: string;
  /** Parsed data (especially for GS1 barcodes) */
  parsedData?: Record<string, unknown>;
  /** Raw barcode value without prefix/suffix */
  rawValue?: string;
}

/**
 * GS1 Application Identifier parsed data
 */
export interface GS1ParsedElement {
  /** Application Identifier code */
  ai: string;
  /** AI description */
  label: string;
  /** Parsed value */
  value: string;
  /** Original segment in barcode */
  raw: string;
}

/**
 * Barcode generation options
 */
export interface BarcodeGenerationOptions {
  /** Barcode type to generate */
  type: BarcodeType;
  /** Value to encode */
  value: string;
  /** Whether to include check digit */
  includeCheckDigit?: boolean;
  /** Prefix to add */
  prefix?: string;
  /** Suffix to add */
  suffix?: string;
  /** For GS1 barcodes: AI data to encode */
  gs1Data?: Record<string, string>;
}

// ============================================================================
// UI LABELS
// ============================================================================

/**
 * Barcode type labels for UI
 */
export const BarcodeTypeLabels: Record<BarcodeType, string> = {
  [BarcodeType.CODE128]: 'Code 128',
  [BarcodeType.CODE39]: 'Code 39',
  [BarcodeType.CODE93]: 'Code 93',
  [BarcodeType.EAN_13]: 'EAN-13',
  [BarcodeType.EAN_8]: 'EAN-8',
  [BarcodeType.UPC_A]: 'UPC-A',
  [BarcodeType.UPC_E]: 'UPC-E',
  [BarcodeType.ITF_14]: 'ITF-14',
  [BarcodeType.GS1_128]: 'GS1-128 (UCC/EAN-128)',
  [BarcodeType.GS1_DATAMATRIX]: 'GS1 DataMatrix',
  [BarcodeType.GS1_QR]: 'GS1 QR Code',
  [BarcodeType.QR_CODE]: 'QR Code',
  [BarcodeType.DATAMATRIX]: 'Data Matrix',
  [BarcodeType.PDF417]: 'PDF417',
  [BarcodeType.PHARMACODE]: 'Pharmacode',
  [BarcodeType.AZTEC]: 'Aztec',
  [BarcodeType.CODABAR]: 'Codabar',
  [BarcodeType.CODE11]: 'Code 11',
};

/**
 * Barcode usage labels for UI
 */
export const BarcodeUsageLabels: Record<BarcodeUsage, string> = {
  [BarcodeUsage.PRODUCT_IDENTIFICATION]: 'Product Identification',
  [BarcodeUsage.SHIPPING_CONTAINER]: 'Shipping Container',
  [BarcodeUsage.LOGISTICS_UNIT]: 'Logistics Unit',
  [BarcodeUsage.SERIAL_NUMBER]: 'Serial Number',
  [BarcodeUsage.LOT_TRACKING]: 'Lot/Batch Tracking',
  [BarcodeUsage.LOCATION]: 'Location',
  [BarcodeUsage.ASSET]: 'Asset',
  [BarcodeUsage.PALLET]: 'Pallet',
  [BarcodeUsage.CARTON]: 'Carton',
};

// ============================================================================
// GS1 APPLICATION IDENTIFIERS
// ============================================================================

/**
 * GS1 Application Identifiers (AI) for GS1-128 barcodes
 * Common AIs used in logistics and supply chain
 */
export const GS1_APPLICATION_IDENTIFIERS = {
  '00': 'SSCC (Serial Shipping Container Code)',
  '01': 'GTIN (Global Trade Item Number)',
  '02': 'GTIN of Contained Trade Items',
  '10': 'Batch/Lot Number',
  '11': 'Production Date (YYMMDD)',
  '12': 'Due Date (YYMMDD)',
  '13': 'Packaging Date (YYMMDD)',
  '15': 'Best Before Date (YYMMDD)',
  '16': 'Expiration Date (YYMMDD)',
  '17': 'Expiration Date (YYMMDD)',
  '20': 'Internal Product Variant',
  '21': 'Serial Number',
  '22': 'Consumer Product Variant',
  '23': 'Additional Product Identification',
  '24': 'Customer Part Number',
  '25': 'Customer Part Number (Extended)',
  '30': 'Variable Count of Items',
  '37': 'Count of Trade Items',
  '40': 'Customer Purchase Order Number',
  '41': 'Ship To/Deliver To Location Code',
  '42': 'Ship From Location Code',
  '43': 'Ship To Post Code',
  '70': 'NATO Stock Number',
  '71': 'Importer ID',
  '72': 'Exporter ID',
  '410': 'Ship To Location (GS1)',
  '411': 'Bill To Location',
  '412': 'Purchase From Location',
  '413': 'Ship For Deliver To Location',
  '414': 'Identification of a Physical Location',
  '415': 'Payment Slip Reference',
  '416': 'Product/Service Location',
  '420': 'Ship To Post Code',
  '421': 'Ship To Post Code with ISO Country',
  '422': 'Country of Origin',
  '423': 'Country of Initial Processing',
  '424': 'Country of Processing',
  '425': 'Country of Disassembly',
  '426': 'Country of Full Process Chain',
  '7001': 'NATO Stock Number',
  '7002': 'Meat Cut',
  '7003': 'Expiration Date with Time',
  '7004': 'Active Potency',
  '703s': 'Processor Approval (7030-7039)',
  '710': 'National Healthcare Reimbursement Number',
  '711': 'National Healthcare Reimbursement Number (Price)',
  '8001': 'Roll Products (Width, Length, Core)',
  '8002': 'Cellular Mobile Telephone Identifier',
  '8003': 'Global Returnable Asset Identifier',
  '8004': 'Global Individual Asset Identifier',
  '8005': 'Price per Unit of Measure',
  '8006': 'Identification of Component Parts',
  '8007': 'International Bank Account Number',
  '8008': 'Date and Time of Production',
  '8009': 'Optically Readable Sensor Data',
  '8010': 'CPID (Component/Part Identifier)',
  '8011': 'CPID Serial Number',
  '8012': 'Software Version',
  '8013': 'Global Model Number',
  '8017': 'Global Service Relation Number',
  '8018': 'Global Service Relation Number',
  '8019': 'Global Service Relation Number (Checksum)',
  '8020': 'Payment Slip Reference',
  '8110': 'Coupon Extended Code',
  '8111': 'Loyalty Points',
  '8112': 'Product Coupon',
  '8200': 'Extended Packaging URL',
  '90': 'Mutually Agreed (Trading Partners)',
  '91': 'Company Internal Information',
  '92': 'Company Internal Information',
  '93': 'Company Internal Information',
  '94': 'Company Internal Information',
  '95': 'Company Internal Information',
  '96': 'Company Internal Information',
  '97': 'Company Internal Information',
  '98': 'Company Internal Information',
  '99': 'Company Internal Information',
} as const;

/**
 * Variable length GS1 AIs (need delimiter or known length)
 */
export const GS1_VARIABLE_LENGTH_AIS = [
  '10',
  '21',
  '22',
  '23',
  '24',
  '25',
  '30',
  '37',
  '40',
  '90',
  '91',
  '92',
  '93',
  '94',
  '95',
  '96',
  '97',
  '98',
  '99',
] as const;

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Barcode type values for validation
 */
export const BARCODE_TYPE_VALUES = [
  'CODE128',
  'CODE39',
  'CODE93',
  'EAN_13',
  'EAN_8',
  'UPC_A',
  'UPC_E',
  'ITF_14',
  'GS1_128',
  'GS1_DATAMATRIX',
  'GS1_QR',
  'QR_CODE',
  'DATAMATRIX',
  'PDF417',
  'PHARMACODE',
  'AZTEC',
  'CODABAR',
  'CODE11',
] as const;

/**
 * Barcode usage values for validation
 */
export const BARCODE_USAGE_VALUES = [
  'PRODUCT_IDENTIFICATION',
  'SHIPPING_CONTAINER',
  'LOGISTICS_UNIT',
  'SERIAL_NUMBER',
  'LOT_TRACKING',
  'LOCATION',
  'ASSET',
  'PALLET',
  'CARTON',
] as const;

/**
 * Default barcode type for new configurations
 */
export const DEFAULT_BARCODE_TYPE = BarcodeType.CODE128;

/**
 * Maximum barcode length
 */
export const MAX_BARCODE_LENGTH = 48;

/**
 * GS1 FNC1 character (used as delimiter in GS1-128)
 */
export const GS1_FNC1 = '\x1D';
